<template>
  <v-container fluid>
    <v-card outlined>
      <v-toolbar flat>
        <!-- No quick search and no creation date criterion: the api filters documents on
             entity_type, entity_id and type only. Rather than offering inputs that silently return
             an unfiltered page, they are left out until the api can apply them. -->
        <v-badge :content="activeFilterCount" :value="activeFilterCount" color="primary" overlap>
          <v-btn outlined color="primary" :aria-label="$t('documents.filter')" @click="filtersDialog = true">
            <v-icon>mdi-filter-plus-outline</v-icon>
          </v-btn>
        </v-badge>

        <v-spacer />
        <v-btn outlined color="primary" :aria-label="$t('documents.reload')" @click="reloadDocuments">
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
      </v-toolbar>

      <v-data-table
        :headers="headers"
        :items="documents"
        :loading="loading"
        :loading-text="$t('order.documents.documentsLoading')"
        :server-items-length="totalDocuments"
        :options.sync="options"
        :footer-props="footerProps"
        :no-data-text="loadError ? $t('documents.loadingError') : $t('documents.noData')"
        item-key="_id"
        dense
        mobile-breakpoint="650"
      >
        <template v-slot:item.name="{ item }">
          <a
            :href="item.url"
            target="_blank"
            rel="noopener noreferrer"
            class="text-decoration-none"
            :class="item.available ? 'primary--text' : 'error--text'"
            @click="openDocument(item, $event)"
          >
            {{ item.name }}
          </a>
        </template>

        <template v-slot:item.entity_type="{ item }">
          {{ displayValue(displayEntityType(item.entity_type)) }}
        </template>

        <!-- The order detail is the only attachment the back-office can open, and it requires
             isCustomerService: any other case falls back to plain text rather than a dead link. -->
        <template v-slot:item.entity_id="{ item }">
          <v-btn
            v-if="item.entity_id && item.entity_type === 'orders' && isCustomerService"
            class="px-0"
            text
            color="primary"
            @click="goToOrder(item.entity_id)"
          >
            {{ item.entity_id }}
          </v-btn>
          <span v-else-if="item.entity_id">{{ item.entity_id }}</span>
          <span v-else>-</span>
        </template>

        <template v-slot:item.location_id="{ item }">
          {{ displayValue(item.location_id) }}
        </template>

        <template v-slot:item.created_at="{ item }">
          <span v-if="item.created_at">{{ new Date(item.created_at) | moment('L') }}</span>
          <span v-else>-</span>
        </template>

        <!-- Dynamic slot per configured custom column (Vue 2.6): the `:key` must sit on the
             rendered child, not on the `<template>` itself, which cannot carry one. No date/number
             formatting is applied here: the raw value is shown as-is, since interpreting a custom
             field's `data_type` is a referential concern, not a rendering one. -->
        <template v-for="column in customColumns" v-slot:[`item.custom.${column.key}`]="{ item }">
          <span :key="column.key">{{ displayValue(customValue(item, column.key)) }}</span>
        </template>

        <template v-slot:item.actions="{ item }">
          <v-btn icon small :aria-label="$t('documents.showValues')" @click="showValues(item)">
            <v-icon>mdi-information-outline</v-icon>
          </v-btn>
        </template>
      </v-data-table>
    </v-card>

    <document-values-dialog v-model="valuesDialog" :fields="detailFields" />
    <filter-dialog
      v-model="filtersDialog"
      :title="$t('documents.filterTitle')"
      :fields="filterFields"
      :loadingData="loading"
      @confirm="filter"
    />

    <v-snackbar v-model="unavailableSnackbar" color="error" timeout="3500">
      {{ $t('documents.unavailable') }}
    </v-snackbar>
  </v-container>
</template>

<script>
  import { mapActions, mapState } from 'vuex'

  import DocumentValuesDialog from '@/components/dialogs/DocumentValuesDialog'
  import { displayMessage } from '@/helpers/helper'
  import {
    DOCUMENT_ENTITY_LABELS,
    DOCUMENT_ENTITY_FILTER_VALUES,
    defaultDocumentDisplay,
    documentActiveCustomFields,
    documentColumnKeys,
    documentCustomFieldLabel,
    documentCustomValues,
    documentDefaultFilters,
  } from '@/helpers/documentDisplay'
  import { DocumentService } from '@/services/documentService'
  import { DocumentSettingsService } from '@/services/documentSettingsService'

  // Document entity enums (RTLOMS-531), in the order the api declares them.
  const DOCUMENT_TYPES = ['invoice', 'credit_note', 'warranty', 'other']
  // Columns dropped in store view: the api restricts the list to the user's own location, so it would
  // repeat the same value on every row.
  const STORE_VIEW_HIDDEN_COLUMNS = new Set(['location_id'])
  const defaultOptions = () => ({
    page: 1,
    itemsPerPage: 15,
    sortBy: ['created_at'],
    sortDesc: [true],
  })

  export default {
    name: 'DocumentsTable',
    components: { DocumentValuesDialog },
    data() {
      return {
        filtersDialog: false,
        valuesDialog: false,
        unavailableSnackbar: false,
        selectedDocument: null,
        loading: false,
        loadError: false,
        totalDocuments: 0,
        options: defaultOptions(),
        documents: [],
        filters: [],
        settings: defaultDocumentDisplay(),
        // v-data-table emits `update:options` on mount, which would otherwise trigger the first
        // `loadDocuments()` before the tenant's default filters are known. The `options` watcher
        // stays a no-op until `loadSettings()` flips this, once the defaults are applied (or a
        // fallback is in place after a failed fetch).
        settingsReady: false,
      }
    },
    created() {
      this.documentService = new DocumentService(this.$auth)
      this.documentSettingsService = new DocumentSettingsService(this.$auth)
      // Not in `data()`: a plain counter used to discard superseded list responses, never rendered,
      // so it has no reason to be reactive.
      this.documentsRequest = 0
      // Same pattern for the configuration: `activated` may start a read while an earlier one is
      // still in flight.
      this.settingsRequest = 0
      // Same reason: a plain flag, raised as soon as the user confirms a selection of their own.
      this.filtersConfirmed = false
      // Same reason: keep-alive bookkeeping, read by `activated` and the `$route` watcher only.
      this.wasActivated = false
      this.leftFromPath = null
      this.leftForOrderPath = null
      this.filters = this.defaultFilters()
      this.loadSettings()
    },
    /**
     * The list is kept alive (MainView). Back from the order detail it opened, it is shown exactly as
     * it was left — page, filters and rows — without a new request. From anywhere else, including the
     * settings screen that may have changed its columns, it starts over from the tenant configuration,
     * as a fresh mount would.
     */
    activated() {
      // Also fired right after the first mount, which `created` already loads.
      if (!this.wasActivated) {
        this.wasActivated = true
        return
      }
      // Still set only if every navigation since `goToOrder` stayed on the list → order → list path.
      const backFromOrder = this.leftForOrderPath !== null
      this.leftForOrderPath = null
      if (backFromOrder) {
        return
      }
      // Lowered first: the options reset below must not fire a request before the defaults are known.
      this.settingsReady = false
      this.filtersConfirmed = false
      this.options = defaultOptions()
      this.loadSettings()
    },
    computed: {
      ...mapState(['isCustomerService', 'currentStore']),
      /**
       * Store view: shop managers and sellers, the users the api scopes to a single location. The
       * role flags auth.js sets are cumulative — octipas-admin, oms-admin and oms-customer-service
       * all raise `isShopManager` too — so `isCustomerService` is the only one that separates the
       * two populations, and its negation covers oms-shop-manager plus the seller carrying no role
       * at all. Same split as the api's own store-employee test.
       */
      isStoreView() {
        return !this.isCustomerService
      },
      /**
       * Store scope forwarded to the service: the store selected in the drawer in store view, null
       * (no restriction) on the central view. The api allowlist-checks the location anyway, so this
       * narrows the scope, never widens it.
       */
      locationId() {
        return this.isStoreView ? this.currentStore : null
      },
      // Custom columns of the list: the configured keys resolved against the display dictionary, in
      // the tenant's configured order (`documentColumnKeys`), not the dictionary order — the two
      // differ once a tenant reorders its columns. The dictionary also holds active keys that are
      // not columns, which the values modal reads and this drops.
      customColumns() {
        const configuredKeys = documentColumnKeys(this.settings)
        const byKey = new Map(documentActiveCustomFields(this.settings).map((field) => [field.key, field]))
        return configuredKeys.filter((key) => byKey.has(key)).map((key) => byKey.get(key))
      },
      /**
       * Columns of the list, built in a single pass over the configured keys so the order the api
       * returns is the order rendered. Standard and custom columns are resolved in the same loop:
       * a custom column a tenant placed between two standard ones now renders there, where
       * appending the custom columns after the standard ones ignored that part of the
       * configuration.
       */
      headers() {
        // Looked up by key so the loop can place a standard and a custom column indifferently.
        const customByKey = new Map(this.customColumns.map((column) => [column.key, column]))
        // entity_type / entity_id / location_id carry the document's attachment context. They are
        // declared here like any other standard key: the tenant configuration is one ordered list,
        // so they take their place inside it rather than being pushed around it.
        const standardHeaders = {
          name: { text: this.$t('documents.name'), value: 'name' },
          type: { text: this.$t('documents.type'), value: 'type' },
          entity_type: { text: this.$t('documents.entityType'), value: 'entity_type' },
          entity_id: { text: this.$t('documents.entityId'), value: 'entity_id' },
          location_id: { text: this.$t('documents.store'), value: 'location_id' },
          created_at: { text: this.$t('documents.createdAt'), value: 'created_at' },
        }
        const headers = []
        for (const key of documentColumnKeys(this.settings)) {
          // Skipping the key in place never reorders the columns around it.
          if (this.isStoreView && STORE_VIEW_HIDDEN_COLUMNS.has(key)) {
            continue
          }
          // A custom key wins over a standard one: the registry reserves type, name and created_at,
          // so only the three attachment keys could collide, and a tenant that declared one of them
          // as a custom field meant its own value.
          const customColumn = customByKey.get(key)
          if (customColumn) {
            headers.push({
              text: documentCustomFieldLabel(customColumn, this.$i18n.locale),
              value: `custom.${key}`,
              // The api sorts on a root-level mongo field; a custom value lives under custom_fields
              // and cannot be sorted server-side.
              sortable: false,
            })
            continue
          }
          // hasOwn, not a truthiness test: a configured key is tenant data, and `constructor` or
          // `toString` would otherwise resolve against the prototype and be pushed as a header.
          if (Object.hasOwn(standardHeaders, key)) {
            headers.push(standardHeaders[key])
          }
        }
        headers.push({ text: '', value: 'actions', sortable: false, align: 'end', width: 64 })
        return headers
      },
      footerProps() {
        return { 'items-per-page-options': [15, 30, 50], 'items-per-page-text': this.$t('documents.perPage') }
      },
      /**
       * Fields offered in the FilterDialog. `type` and `entity_type` are multi-valued: the tenant
       * configuration stores default filters as arrays, and the service now serializes a multi-value
       * selection as repeated query keys, so a multi-select no longer silently drops values.
       * `initialValue` mirrors the current filter (so the dialog reopens on whatever the previous
       * confirm last set) while `defaultValue` is what "Reset" restores.
       * Only the criteria the api can apply are offered: a document name and a creation date range
       * are deliberately absent, since `buildFilter` reads entity_type, entity_id and type only.
       * Both views offer the same criteria: the store view lists every document of the store, so
       * each attachment type, "None" included, can return rows there too.
       */
      filterFields() {
        const defaults = new Map(this.defaultFilters().map((filter) => [filter.name, filter.value]))
        return [
          {
            text: this.$t('documents.type'),
            value: 'type',
            filterType: 'array',
            multiple: true,
            clearable: true,
            arrayValues: DOCUMENT_TYPES.map((type) => ({
              text: this.displayDocumentType(type),
              value: type,
            })),
            initialValue: this.filterValue('type'),
            defaultValue: defaults.get('type'),
          },
          {
            text: this.$t('documents.entityType'),
            value: 'entity_type',
            filterType: 'array',
            multiple: true,
            clearable: true,
            // "None" is one more selectable value, distinct from an empty selection: selected, it
            // sends DOCUMENT_NO_ENTITY alongside the other types and the api returns their union.
            arrayValues: DOCUMENT_ENTITY_FILTER_VALUES.map((entityType) => ({
              text: this.displayEntityType(entityType),
              value: entityType,
            })),
            initialValue: this.filterValue('entity_type'),
            defaultValue: defaults.get('entity_type'),
          },
        ]
      },
      activeFilterCount() {
        return this.filters.filter((filter) => filter.active).length
      },
      /**
       * "Show all values" modal content: the keys of the display dictionary actually carried by the
       * selected document. The dictionary is not scoped to the selected columns nor to the caller's
       * role, so an active key the tenant did not list as a column shows its value here. A key
       * deactivated in the referential leaves the dictionary and disappears from the modal even
       * though the document still stores the value.
       */
      detailFields() {
        const customValues = this.selectedDocument?.customValues || {}
        return documentActiveCustomFields(this.settings)
          .filter((field) => Object.hasOwn(customValues, field.key))
          .map((field) => ({
            key: field.key,
            label: documentCustomFieldLabel(field, this.$i18n.locale),
            value: customValues[field.key].value,
          }))
      },
    },
    watch: {
      options: {
        deep: true,
        handler() {
          if (!this.settingsReady) {
            return
          }
          this.loadDocuments()
        },
      },
      /**
       * The list stays alive while hidden, so it sees every navigation, not only the last one. Any
       * step off the list → order → list path forgets the order, and the way back starts the list
       * over: a detour through the settings screen may have changed its columns. The return itself
       * is kept: this watcher runs before `activated`, which still has to read it.
       */
      $route(to, from) {
        if (this.leftForOrderPath === null) {
          return
        }
        const toOrder = to.path === this.leftForOrderPath
        const backToList = to.path === this.leftFromPath && from.path === this.leftForOrderPath
        if (!toOrder && !backToList) {
          this.leftForOrderPath = null
        }
      },
    },
    methods: {
      ...mapActions(['setBreadcrumbs']),
      displayValue(value) {
        return value === null || value === undefined || value === '' ? '-' : value
      },
      // A document may simply not carry a given custom field at all (as opposed to carrying it
      // with an empty value), hence the optional chaining before reading `.value`.
      customValue(item, key) {
        return item.customValues[key]?.value
      },
      /**
       * Current value of a filter, as a copy when it is an array or an object.
       * FilterDialog assigns `initialValue` straight into its own filter state, and some of its
       * inputs write back into the very value they are handed rather than emitting a new one.
       * Handing out the array stored in `filters` would let the dialog edit it even when the user
       * closes without confirming, so the dialog only ever gets a detached copy.
       *
       * @param {string} name - The filter name.
       * @returns {*} The filter value, copied when it is not a primitive.
       */
      filterValue(name) {
        const value = this.filters.find((filter) => filter.name === name)?.value
        if (value === null || typeof value !== 'object') {
          return value
        }
        return structuredClone(value)
      },
      // The initial filter state and the FilterDialog "Reset" target, from the tenant defaults.
      defaultFilters() {
        return documentDefaultFilters(this.settings)
      },
      /**
       * Reads the tenant display configuration before the first page is fetched: on mount, and again
       * whenever `activated` starts the kept-alive list over. The settings screen is a sibling route
       * rendered in the same router-view, so it never lives alongside this list: coming back from it
       * re-reads the configuration. Leaving and coming back while a read is in flight starts a second
       * one: only the latest answer is applied, a superseded one is discarded, failure included.
       *
       * @returns {Promise<void>}
       */
      async loadSettings() {
        const request = ++this.settingsRequest
        try {
          const settings = await this.documentSettingsService.getSettings()
          if (request !== this.settingsRequest) {
            return
          }
          this.settings = settings
        } catch (error) {
          // Logged even when superseded: only the user-facing message and the state are left to the
          // latest read.
          console.error('Document settings API:', error)
          if (request !== this.settingsRequest) {
            return
          }
          // The fallback the component was initialised with stays in place: the list renders with
          // the default columns rather than not at all.
          displayMessage(this.$t('documents.settingsError'))
        }
        // The tenant's default filters are seeded only here, and only if the user has not already
        // confirmed a selection of their own: the filter dialog is reachable from the first paint,
        // so a slow configuration answer must not replace what they just chose.
        if (!this.filtersConfirmed) {
          this.filters = this.defaultFilters()
        }
        this.settingsReady = true
        this.loadDocuments()
      },
      /**
       * Loads a page of documents, discarding an answer a newer request has superseded: the filter
       * dialog, the reload button and a page change each start a request without waiting for the
       * one in flight, and the slower answer would otherwise show rows that no longer match the
       * filters the screen displays.
       *
       * @returns {Promise<void>}
       */
      async loadDocuments() {
        const request = ++this.documentsRequest
        this.loading = true
        this.loadError = false
        try {
          const response = await this.documentService.list({ ...this.options }, this.filters, this.locationId)
          if (request !== this.documentsRequest) {
            return
          }
          this.documents = response.data.map(this.normalizeDocument)
          this.totalDocuments = response.nbDocuments
        } catch (error) {
          if (request !== this.documentsRequest) {
            return
          }
          console.error('Document API:', error)
          displayMessage(this.$t('documents.loadingError'))
          this.documents = []
          this.totalDocuments = 0
          this.loadError = true
        } finally {
          // Left on while a newer request is still running: the table stays in its loading state
          // until the answer it is waiting for arrives.
          if (request === this.documentsRequest) {
            this.loading = false
          }
        }
      },
      filter(filters) {
        // Raised before anything else: the configuration may still be in flight, and what the user
        // just confirmed outranks the tenant defaults it would otherwise seed.
        this.filtersConfirmed = true
        this.filters = filters
        this.reloadDocuments()
      },
      reloadDocuments() {
        // Going back to page 1 mutates `options`, which the watcher already turns into a load:
        // calling loadDocuments() here too would fire the same request twice.
        if (this.options.page !== 1) {
          this.options.page = 1
          return
        }
        this.loadDocuments()
      },
      showValues(document) {
        this.selectedDocument = document
        this.valuesDialog = true
      },
      normalizeDocument(document) {
        return {
          ...document,
          type: this.displayDocumentType(document.type),
          // A plain object, not the Map documentCustomValues() returns: Vue 2 cannot make a Map's
          // entries reactive.
          customValues: Object.fromEntries(documentCustomValues(document)),
          available: Boolean(document.url),
        }
      },
      displayDocumentType(type) {
        const labels = {
          invoice: 'invoice',
          credit_note: 'creditNote',
          warranty: 'warranty',
          other: 'other',
        }
        return labels[type] ? this.$t(`order.documents.${labels[type]}`) : type
      },
      displayEntityType(entityType) {
        return DOCUMENT_ENTITY_LABELS[entityType] ? this.$t(DOCUMENT_ENTITY_LABELS[entityType]) : entityType
      },
      openDocument(document, event) {
        if (!document.available) {
          event.preventDefault()
          this.unavailableSnackbar = true
        }
      },
      // Order detail (/orders/:id) requires isCustomerService — the link is only rendered
      // for that role, so navigation always lands on the admin/customer-service detail page.
      // The trail is cleared first: the order detail does not reset it and only applies its own
      // parent when it is empty, so it would otherwise read "Documents / Order X" — the order
      // belongs under Orders, wherever the user came from. Same move as AppBar.returnBack().
      goToOrder(orderId) {
        this.setBreadcrumbs([])
        // Remembered so `activated` recognises the return from this very detail.
        this.leftFromPath = this.$route.path
        this.leftForOrderPath = '/orders/' + orderId
        this.$router.push(this.leftForOrderPath)
      },
    },
  }
</script>
